import { Box } from '@mui/material'
import _ from 'lodash'
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

import { BackToTop, ErrorServer, FooterSticky } from '~/components'
import MyCartBar from '~/components/MyCartBar'
import { loadingStates } from '~/config/constant'
import { heightCategory } from '~/constants'
import { selectRestaurant } from '~/redux/restaurantSlice'
import MenuList from './components/MenuList'
import TopCategories from './components/TopCategories'
import { fetchMenu, resetMenu, selectValuesMenu } from './slices/menuSlice'
import { isElectronicMenu } from '~/utils'
import { useAppContext } from '~/contexts/AppContext'
import ScrollTop from '~/components/ScrollTop'

export default function Menu() {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const { heightHeaderFixed } = useAppContext()
  const heightTopSticky = useMemo(() => heightCategory + heightHeaderFixed, [heightHeaderFixed])

  const { data, loading } = useSelector(selectValuesMenu)
  const { restaurantId } = useSelector(selectRestaurant)
  const [searchParams] = useSearchParams()
  const [isScrollFromURL, setIsScrollFromURL] = useState(false) // đánh dấu chỉ cuộn một lần trong trường hợp truyền category_ID trên url từ các màn khác vào màn này
  const categoryIdFromURL = searchParams.get('category_ID')

  const [idActiveCategory, setIdActiveCategory] = useState(null)

  const idTimer = useRef()
  const listCategoriesRef = useRef([])

  /**
   *  Tạo ra mảng listCategoriesRef được đánh index.
   *  Phục vụ cho việc lấy offsetTop của từng item category
   *  */
  listCategoriesRef.current = useMemo(() => {
    return [...Array(data?.length)].map((_, i) => listCategoriesRef.current[i] ?? createRef())
  }, [data?.length])

  // Gọi api load data
  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchMenu(restaurantId))
    }
  }, [i18n.language, dispatch, restaurantId])

  // Xử lý trường hợp unmounted => reset menu tránh cache
  useEffect(() => {
    return () => {
      dispatch(resetMenu())
    }
  }, [dispatch])

  /**
   * Hàm xử lý sự kiện người dùng cuộn tới category bất kỳ => active category tương ứng trên header
   * Chú ý: ở đây dùng debouce để lúc người dùng cuộn nhanh thì chỉ lấy giá cuối cùng tránh lãng phí bộ nhớ
   */
  const handleActiveCategory = useCallback(
    (windowOffsetY) => {
      if (idTimer.current) clearTimeout(idTimer.current)

      idTimer.current = setTimeout(() => {
        // Xử lý lần đầu scan qr code theo category sẽ ưu tiên active category trên topbar category
        if (!isScrollFromURL && categoryIdFromURL) {
          setIdActiveCategory(Number(categoryIdFromURL) || categoryIdFromURL) // Chỗ này do Id có thể là video_menu khi convert về number sẽ thành NaN
          setIsScrollFromURL(true)
          return
        }

        // Xử lý đối với trường hợp active category khi cuộn bình thường
        const itemCategory = _.findLast(listCategoriesRef.current, (item) => {
          return windowOffsetY + heightTopSticky >= item?.current?.getOffsetTop()
        })

        const idCategoryActive = itemCategory?.current?.getIdCategory()

        if (idCategoryActive) setIdActiveCategory(idCategoryActive)

        // Xử lý trường hợp người dùng back to top hoặc người dùng cuộn nhanh từ dưới cùng lên mà không thả tay ra
        if (!idCategoryActive && windowOffsetY === 0) {
          const itemCategory = listCategoriesRef.current[0]
          const idCategory = itemCategory?.current?.getIdCategory()
          setIdActiveCategory(idCategory)
        }
      }, 100)
    },
    [isScrollFromURL, categoryIdFromURL, heightTopSticky],
  )

  // Lắng nghe sự kiện cuộn của người dùng
  useEffect(() => {
    const scrollHandler = () => {
      const windowOffsetY = window.scrollY

      window.requestAnimationFrame(function () {
        handleActiveCategory(windowOffsetY)
      })
    }

    window.addEventListener('scroll', scrollHandler)

    return () => {
      window.removeEventListener('scroll', scrollHandler)

      if (idTimer.current) clearTimeout(idTimer.current)
    }
  }, [handleActiveCategory])

  // Hàm xử lý khi người dùng click vào từng category thì cuộn xuống tương ứng
  const onClickItemCategory = useCallback(
    (index) => {
      const totalHeightTopSticky = heightTopSticky

      const itemCategoryRef = listCategoriesRef?.current?.[index]

      const top = itemCategoryRef?.current?.getOffsetTop() - totalHeightTopSticky
      window.scrollTo({
        left: 0,
        top,
        behavior: 'smooth',
      })
    },
    [heightTopSticky],
  )

  const renderHeaderCategory = () => {
    return (
      <TopCategories
        top={heightHeaderFixed}
        idActiveCategory={idActiveCategory}
        categories={data}
        onClickItemCategory={onClickItemCategory}
      />
    )
  }

  // Hiển thị icon scroll to top
  const renderScrollTopIcon = () => {
    return <ScrollTop />
  }

  const renderListMenuFood = () => {
    return (
      <Box paddingBottom={16} paddingTop={`${heightCategory}px`} component="div" minHeight="100vh">
        {data.length > 0 &&
          data.map((category, index) => (
            <MenuList ref={listCategoriesRef.current?.[index]} {...category} key={index} />
          ))}
        {renderScrollTopIcon()}
        {restaurantId && loading === loadingStates.success && <BackToTop />}
      </Box>
    )
  }

  if (loading === loadingStates.error) return <ErrorServer />

  return (
    <Box maxWidth={'md'} marginX={'auto'}>
      {renderHeaderCategory()}
      {renderListMenuFood()}
      {!isElectronicMenu() && (
        <FooterSticky>
          <MyCartBar />
        </FooterSticky>
      )}
    </Box>
  )
}
